import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  TextField,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { Field, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';
import ReportConfigClient from '../../clients/ReportConfigClient';
import {
  ReportFilter,
  ReportFilterInputType,
  ReportFilterOperator,
} from '../../models/report-details';
import { ReportFilterOption } from '../../models/report-filter';
import { BaseQueryColumnInfo } from '../../models/report-info';
import { CubitCheckboxAdapter } from '../cubit-inputs/react-form-adapters/cubit-checkbox-adapter';
import { CubitSelectAdapter } from '../cubit-inputs/react-form-adapters/cubit-select-adapter';
import { CubitTextFieldAdapter } from '../cubit-inputs/react-form-adapters/cubit-text-field-adapter';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import { CubitAutocompleteAdapter } from '../cubit-inputs/react-form-adapters/cubit-autocomplete-adapter';
import {
  BooleanSelectItems,
  ClusionTypeItems,
  InputTypeItems,
  OperatorSelectItems,
  getClusionTitle,
  getColumnInfo,
  getDefaultValue,
  getFieldComponent,
  getFieldNameByOperator,
  getFilterType,
  getOperatorValue,
  sqlColumnIsListType,
} from './filter-edit-helper';
import { ColumnText, processedColumns } from './config-helper';
import React from 'react';
import ReportsClient from '../../clients/ReportsClient';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { ReportsActions } from '../../redux/reports-actions';

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {
    margin: '8px 0 16px ',
    minWidth: '80%',
  },
  formField: {
    width: '100%',
  },
  loader: {
    position: 'relative',
    width: 'calc(80% + 16px)',
    textAlign: 'center',
    margin: '16px 0',
  },
  editIconContainer: {
    width: '100%',
    borderBottom: 'solid #000 1px',
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  fieldValueContainer: {
    display: 'flex',
    padding: '12px 0 0 24px',
  },
  field: {
    minWidth: '200px',
    color: ' rgba(0, 0, 0, 0.55)',
  },
  value: {
    marginLeft: '5px',
  },
}));
const SelectedFilterForm: React.FC<
  InjectedFormProps<ReportFilter> & {
    baseQueryCode: string;
    potentialColumns: BaseQueryColumnInfo[];
    setSelectedFilter: any;
    selectedFilter: any;
    setNewFilter: any;
    newFilter: any;
    newFilters: any;
    selectedReport: any;
    setHasUnsavedChanges: any;
    index: number;
    open: boolean;
    baseQuery: string;
  }
> = (props) => {
  const {
    handleSubmit,
    setSelectedFilter,
    selectedFilter,
    form,
    baseQueryCode,
    potentialColumns,
    newFilters,
    baseQuery,
  } = props;
  const styles = useStyles();
  const [isOpen, setOpen] = useState(newFilters?.length ? true : false);
  const [columnValues, setColumnValues] = useState<ReportFilterOption[]>([]);
  const [rerenderKey, setRerenderKey] = useState(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const columnText = ColumnText(t);
  const values = useSelector((state) =>
    getFormValues(form)(state)
  ) as ReportFilter;
  useEffect(() => {
    if (values?.sqlColumn && sqlColumnIsListType(potentialColumns, values)) {
      setColumnValues(null);
      ReportConfigClient.getBaseQueryColumnValues(
        baseQueryCode,
        values?.sqlColumn
      ).subscribe((x) => {
        setColumnValues(x);
      });
    }
  }, [baseQueryCode, potentialColumns, values?.sqlColumn]);

  useEffect(() => {
    if (columnValues) {
      setRerenderKey((k) => k + 1);
    }
  }, [columnValues]);

  const ClusionItemsAutocomplete = ({ filterId, options, input, ...rest }) => {
    const [allOptions, setAllOptions] = useState(options);
    const [selectedOptions, setSelectedOptions] = useState(
      input.value || rest.meta.initial || []
    );

    useEffect(() => {
      let isMounted = true;

      const fetchAllOptions = (page = 2) => {
        ReportsClient.loadOptions(filterId, page).subscribe(
          (response) => {
            if (isMounted) {
              const newOptions = response?.map((option) => option.value);
              setAllOptions((prevOptions) => [...prevOptions, ...newOptions]);
              if (response?.length === 0) {
                return;
              }
              fetchAllOptions(page + 1);
            }
          },
          (error) => {
            if (isMounted) {
              console.error(error);
            }
          }
        );
      };

      fetchAllOptions();

      return () => {
        isMounted = false;
      };
    }, [filterId]);

    const handleCheckboxSelectOption = (option) => {
      dispatch(ReportsActions.actionSetHasMoreOptions(false));

      const currentIndex = selectedOptions.indexOf(option);
      const newSelectedOptions = [...selectedOptions];
      if (currentIndex === -1) {
        newSelectedOptions.push(option);
      } else {
        newSelectedOptions.splice(currentIndex, 1);
      }
      setSelectedOptions(newSelectedOptions);
    };

    const handleSelectOption = (option) => {
      dispatch(ReportsActions.actionSetHasMoreOptions(false));
      const currentIndex = selectedOptions.indexOf(option);
      const newSelectedOptions = [...selectedOptions];
      if (currentIndex === -1) {
        newSelectedOptions.push(option);
      } else {
        newSelectedOptions.splice(currentIndex, 1);
      }
      setSelectedOptions(option);
      input.onChange(option);
    };

    const handleRemoveOption = (option) => {
      dispatch(ReportsActions.actionSetHasMoreOptions(false));

      const currentIndex = selectedOptions.indexOf(option);
      const newSelectedOptions = [...selectedOptions];
      if (currentIndex === -1) {
        newSelectedOptions.push(option);
      } else {
        newSelectedOptions.splice(currentIndex, 1);
      }
      setSelectedOptions(newSelectedOptions);
      input.onChange(newSelectedOptions);
    };

    const renderTags = (value, getTagProps) =>
      value.map((option, index) => (
        <Chip
          size='small'
          label={option}
          {...getTagProps({ index })}
          onDelete={() => handleRemoveOption(option)}
        />
      ));

    const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
    const checkedIcon = <CheckBoxIcon fontSize='small' />;

    return (
      <Autocomplete
        {...input}
        {...rest}
        id='clusionItems'
        size='small'
        limitTags={10}
        options={allOptions}
        onChange={(event, newValue) => {
          handleSelectOption(newValue);
        }}
        renderTags={renderTags}
        onBlur={() => {
          input.onChange(selectedOptions);
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
              onClick={(event) => {
                event.stopPropagation();
                handleCheckboxSelectOption(option);
              }}
            />
            {option}
          </li>
        )}
        value={selectedOptions || rest.meta.initial || []}
        multiple
        getOptionLabel={(option) => option || ''}
        renderInput={(params) => (
          <TextField
            {...params}
            variant='standard'
            label={rest.label}
            placeholder='Clusion items'
            onFocus={(e) => e.target.click()}
          />
        )}
      />
    );
  };

  const clusionItems =
    values.jsonParams && JSON.parse(values?.jsonParams).clusionItems;

  const clusionType =
    values.jsonParams && JSON.parse(values?.jsonParams).clusionType;

  return (
    <>
      <div className={styles.editIconContainer}>
        <div>{values?.displayName}</div>
        <div>
          <IconButton onClick={() => setOpen(true)}>
            <EditIcon />
          </IconButton>
        </div>
      </div>
      <div>
        <div className={styles.fieldValueContainer}>
          <div className={styles.field}>{t('SQL_COLONNE', 'SQL kolonne')}</div>
          <div className={styles.value}>
            {columnText(selectedFilter?.sqlColumn)}
          </div>
        </div>
        <div className={styles.fieldValueContainer}>
          <div className={styles.field}>{t('FILTER_TYPE', 'Filtertype')}</div>
          <div className={styles.value}>
            {getFilterType(values?.reportInputType, t)}
          </div>
        </div>
        <div className={styles.fieldValueContainer}>
          <div className={styles.field}>{t('OPERATOR', 'Operatør')}</div>
          <div className={styles.value}>
            {getOperatorValue(values?.operator, t)}
          </div>
        </div>
        {values?.reportInputType === ReportFilterInputType.MultiSelect ||
        values?.reportInputType === ReportFilterInputType.SelectList ? (
          <div className={styles.fieldValueContainer}>
            <div className={styles.field}>
              <div>{`${getClusionTitle(clusionType, t)}`}</div>
            </div>
            <div>
              {clusionType &&
                clusionType !== 'all' &&
                clusionItems?.map((item: string, index: number) => (
                  <Chip sx={{ m: '5px' }} key={index} label={item} />
                ))}
            </div>
          </div>
        ) : null}
        {values?.reportInputType &&
          values?.reportInputType !== ReportFilterInputType.MultiSelect &&
          values?.reportInputType !== ReportFilterInputType.SelectList && (
            <>
              {values.operator !== ReportFilterOperator.InBetween &&
                values.reportInputType !== ReportFilterInputType.Boolean &&
                getDefaultValue(values.operator, values) && (
                  <div className={styles.fieldValueContainer}>
                    <div className={styles.field}>
                      {t('DEFAULT_VALUE', 'standardverdi')}
                    </div>
                    <div className={styles.value}>
                      {getDefaultValue(values.operator, values)}
                    </div>
                  </div>
                )}
              {values.reportInputType === ReportFilterInputType.Boolean && (
                <div className={styles.fieldValueContainer}>
                  <div className={styles.field}>
                    {t('DEFAULT_VALUE', 'Standardverdi')}
                  </div>
                  <div className={styles.value}>
                    {getDefaultValue(values.operator, values)}
                  </div>
                </div>
              )}
              {values.operator === ReportFilterOperator.InBetween && (
                <>
                  <div className={styles.fieldValueContainer}>
                    <div className={styles.field}>
                      {t('DEFAULT_VALUE_MINIMUM', 'Minimum standardverdi')}
                    </div>
                    <div className={styles.value}>{values.defaultValueMin}</div>
                  </div>
                  <div className={styles.fieldValueContainer}>
                    <div className={styles.field}>
                      {t('DEFAULT_VALUE_MAXIMUM', 'Maksimal standardverdi')}
                    </div>
                    <div className={styles.value}>{values.defaultValueMax}</div>
                  </div>
                </>
              )}

              <div className={styles.fieldValueContainer}>
                <div className={styles.field}>{t('HIDDEN', 'Skjult')}</div>
                <div className={styles.value}>
                  {values.isHidden === true ? 'Ja' : 'Nei'}
                </div>
              </div>
            </>
          )}
      </div>
      {isOpen && (
        <Dialog
          key={rerenderKey}
          sx={{ padding: '16px' }}
          maxWidth={'xs'}
          fullWidth={true}
          open={isOpen}
          onClose={() => setOpen(false)}>
          <DialogTitle>{t('EDIT_FILTER', 'Endre filter')}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <div style={{ height: '100%' }}>
                <div style={{ flex: 1 }}>
                  <div className={styles.formControl}>
                    <div>
                      <Field
                        formcontrolprops={{
                          className: styles.formField,
                        }}
                        name='displayName'
                        label={t('TITLE', 'Tittel')}
                        component={CubitTextFieldAdapter}
                      />
                    </div>
                  </div>
                  <div className={styles.formControl}>
                    <div>
                      <Field
                        type='select'
                        formcontrolprops={{
                          className: styles.formField,
                        }}
                        initialValue={values?.sqlColumn}
                        defaultValue={values?.sqlColumn}
                        setSelectedFilter={setSelectedFilter}
                        selectedFilter={selectedFilter}
                        name='sqlColumn'
                        label={t('SQL_COLUMN', 'SQL kolonne')}
                        component={CubitAutocompleteAdapter}
                        options={processedColumns(
                          baseQuery,
                          potentialColumns,
                          t
                        )}
                      />
                    </div>
                  </div>

                  <div className={styles.formControl}>
                    {selectedFilter.sqlColumn && (
                      <div>
                        <Field
                          type='select'
                          formcontrolprops={{
                            className: styles.formField,
                          }}
                          name='reportInputType'
                          label={t('FILTER_TYPE', 'Filtertype')}
                          valueisobject={false}
                          component={CubitSelectAdapter}
                          options={InputTypeItems(t).filter((x) =>
                            x.dataTypes.includes(
                              getColumnInfo(potentialColumns, selectedFilter)
                                ?.dataType
                            )
                          )}
                        />
                      </div>
                    )}
                    {selectedFilter.sqlColumn && (
                      <div className={styles.formControl}>
                        <Field
                          type='select'
                          formcontrolprops={{ className: styles.formField }}
                          name='operator'
                          label={t('OPERATOR', 'Operatør')}
                          valueisobject={false}
                          component={CubitSelectAdapter}
                          options={OperatorSelectItems(t).filter(
                            (x) =>
                              values.reportInputType &&
                              x.allowedInputTypes.includes(
                                values.reportInputType
                              )
                          )}
                        />
                      </div>
                    )}
                  </div>
                  {values.reportInputType &&
                    values.reportInputType !==
                      ReportFilterInputType.MultiSelect &&
                    values.reportInputType !==
                      ReportFilterInputType.SelectList && (
                      <>
                        <div className={styles.formControl}>
                          {values.operator !== ReportFilterOperator.InBetween &&
                            values.reportInputType !==
                              ReportFilterInputType.Boolean && (
                              <div>
                                <Field
                                  formcontrolprops={{
                                    className: styles.formField,
                                  }}
                                  name={getFieldNameByOperator(
                                    values.operator,
                                    t
                                  )}
                                  label={t('DEFAULT_VALUE', 'Standardverdi')}
                                  component={getFieldComponent(6)}
                                />
                              </div>
                            )}
                          {values.reportInputType ===
                            ReportFilterInputType.Boolean && (
                            <div>
                              <Field
                                type='select'
                                formcontrolprops={{
                                  className: styles.formField,
                                }}
                                name='defaultValueMin'
                                label={t('DEFAULT_VALUE', 'Standardverdi')}
                                valueisobject={false}
                                component={CubitSelectAdapter}
                                options={BooleanSelectItems(t)}
                              />
                            </div>
                          )}
                          {values.operator ===
                            ReportFilterOperator.InBetween && (
                            <>
                              <div>
                                <Field
                                  formcontrolprops={{
                                    className: styles.formField,
                                  }}
                                  name='defaultValueMin'
                                  label={t(
                                    'DEFAULT_VALUE_MINIMUM',
                                    'Minimum standardverdi'
                                  )}
                                  component={getFieldComponent(6)}
                                />
                              </div>
                              <div>
                                <Field
                                  formcontrolprops={{
                                    className: styles.formField,
                                  }}
                                  name='defaultValueMax'
                                  label={t(
                                    'DEFAULT_VALUE_MAXIMUM',
                                    'Maksimal standardverdi'
                                  )}
                                  component={getFieldComponent(6)}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        <FormControl
                          variant='standard'
                          className={styles.formControl}>
                          <Field
                            name='isHidden'
                            label={t('HIDDEN', 'Skjult')}
                            component={CubitCheckboxAdapter}
                          />
                        </FormControl>
                      </>
                    )}

                  {!columnValues && (
                    <div className={styles.loader}>
                      <TailSpin
                        wrapperStyle={{ display: 'inline-block' }}
                        color='#385690'
                        height={32}
                        width={32}
                      />
                    </div>
                  )}
                  {columnValues && (
                    <>
                      {((sqlColumnIsListType(potentialColumns, values) &&
                        values.reportInputType ===
                          ReportFilterInputType.MultiSelect) ||
                        values.reportInputType ===
                          ReportFilterInputType.MultiSelectText ||
                        values.reportInputType ===
                          ReportFilterInputType.SelectList) &&
                        selectedFilter.id && (
                          <div>
                            <Field
                              type='select'
                              formcontrolprops={{
                                className: styles.formField,
                              }}
                              name='clusionType'
                              label={t(
                                'INCLUDE_OR_EXCLUDE_OPTIONS',
                                'Inkludere/ekskludere alternativer'
                              )}
                              valueisobject={false}
                              component={CubitSelectAdapter}
                              options={ClusionTypeItems(t)}
                            />
                          </div>
                        )}
                      {values.clusionType !== 'all' &&
                        sqlColumnIsListType(potentialColumns, values) &&
                        (values.reportInputType ===
                          ReportFilterInputType.MultiSelect ||
                          values.reportInputType ===
                            ReportFilterInputType.MultiSelectText ||
                          values.reportInputType ===
                            ReportFilterInputType.SelectList) && (
                          <div className={styles.formControl}>
                            <Field
                              type='select'
                              formcontrolprops={{
                                className: styles.formField,
                              }}
                              name='clusionItems'
                              label={
                                values.clusionType === 'include'
                                  ? t('INCLUDE_VALUES', 'Inkludere verdier')
                                  : t('EXCLUDE_VALUES', 'Ekskluder verdier')
                              }
                              filterId={selectedFilter.id}
                              component={ClusionItemsAutocomplete}
                              options={columnValues.map((x) => x.value)}
                            />
                          </div>
                        )}
                    </>
                  )}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button variant={'text'} onClick={() => setOpen(false)}>
                {t('CANCEL', 'Avbryt')}
              </Button>
              <Button
                variant={'contained'}
                onClick={handleSubmit}
                type='submit'>
                {t('save_filter', 'Lagre filter')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
    </>
  );
};
export const SelectedFilterBlock = reduxForm<
  ReportFilter,
  {
    potentialColumns: BaseQueryColumnInfo[];
    baseQuery: string;
    baseQueryCode: string;
    index: any;
    setSelectedFilter: any;
    selectedFilter: any;
    setNewFilters: any;
    newFilters: any;
    selectedReport: any;
    setHasUnsavedChanges: any;
    open: boolean;
  }
>({
  form: 'selectedFilterForm',
  enableReinitialize: false,
})(SelectedFilterForm as any);
